import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  ButtonBaseActions,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  Paper,
  Typography, ListItemButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalAddRole from './ModalAddRole';
import { DeleteConfirmationModal } from 'components/delete-confirmation';
import { Roles } from '../types';
import { ListingEntry } from 'graphql/listings/queries';
import { createRoleParams, deleteRoleParams } from '../services/mutations';

const coarsePointer = '@media (hover: none) and (pointer: coarse)';

const useStyles = makeStyles((theme) => ({
  containerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  title: {
    minWidth: 0,
    overflowWrap: 'anywhere',
    color: theme.palette.text.primary,
  },
  addIcon: {
    color: theme.palette.primary.main,
  },
  addButton: {
    flexShrink: 0,
    [coarsePointer]: {
      minWidth: theme.spacing(5.5),
      minHeight: theme.spacing(5.5),
    },
  },
  listItem: {
    padding: theme.spacing(2),
    // Reserve room for the absolutely positioned delete action.
    paddingRight: theme.spacing(6),
    [coarsePointer]: {
      minHeight: theme.spacing(5.5),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingRight: theme.spacing(8),
    },
  },
  listItemText: {
    minWidth: 0,
    fontSize: '14px',
    overflowWrap: 'anywhere',
    color: theme.palette.text.primary,
  },
  deleteAction: { right: theme.spacing(1) },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  deleteButton: {
    padding: theme.spacing(1),
    [coarsePointer]: {
      minWidth: theme.spacing(5.5),
      minHeight: theme.spacing(5.5),
    },
  },
}));

// ListItem autoFocus targets the <li> container when a secondary action exists, so focus the row button directly.
const focusRow = (actions: ButtonBaseActions | null) => actions?.focusVisible();

interface ListRolesProps {
  roles: Roles[];
  listingQueries: ListingEntry[];
  listingMutations: ListingEntry[];
  handleCreateRole: (data: createRoleParams) => void;
  handleDeleteRole: (data: deleteRoleParams) => void;
  selectedRole: Roles | undefined;
  setSelectedRole: React.Dispatch<React.SetStateAction<Roles | undefined>>;
  focusRoleId?: string;
}

const ListRoles: FC<ListRolesProps> = ({
  roles,
  listingQueries,
  listingMutations,
  handleCreateRole,
  handleDeleteRole,
  selectedRole,
  setSelectedRole,
  focusRoleId,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState({
    status: false,
    id: '',
  });

  const toggleModal = () => {
    setOpen(!open);
  };

  const handleOpenDeleteModal = (id: string) => {
    setOpenDelete({
      status: true,
      id,
    });
  };

  const handleCloseDeleteModal = () => {
    setOpenDelete({
      status: false,
      id: '',
    });
  };

  return (
    <Paper>
      <Box className={classes.containerHeader}>
        <Typography variant="h6" className={classes.title}>
          {t('roles:listOfRoles')}
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          disableElevation
          onClick={toggleModal}
          className={classes.addButton}
          aria-label={t('roles:addNewRoleTitle')}
        >
          <FontAwesomeIcon icon="plus" className={classes.addIcon} />
        </Button>
      </Box>
      <List>
        {roles.map((role) => (
          <ListItemButton
            key={role._id}
            className={classes.listItem}
            onClick={() => setSelectedRole(role)}
            selected={selectedRole?._id === role._id}
            action={focusRoleId === role._id ? focusRow : undefined}
          >
            <Typography className={classes.listItemText} variant="subtitle2">
              {role.description}
            </Typography>
            <ListItemSecondaryAction className={classes.deleteAction}>
              <IconButton
                className={classes.deleteButton}
                aria-label={t('roles:deleteRoleLabel', { description: role.description })}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDeleteModal(role._id);
                }}
                size="large"
              >
                <FontAwesomeIcon icon={faTrash} size="xs" className={classes.deleteIcon} />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItemButton>
        ))}
      </List>
      {open && (
        <ModalAddRole
          open={open}
          handleClose={toggleModal}
          listingQueries={listingQueries}
          listingMutations={listingMutations}
          handleCreateRole={handleCreateRole}
        />
      )}
      <DeleteConfirmationModal
        isOpen={openDelete.status}
        onClose={handleCloseDeleteModal}
        title={t('roles:deleteRoleTitle')}
        confirmationMessage={t('roles:deleteRoleSubtitle')}
        buttonConfirmText={t('common:delete')}
        isRemoveLoading={false}
        onConfirm={() => {
          handleDeleteRole({ id: openDelete.id });
          handleCloseDeleteModal();
        }}
      />
    </Paper>
  );
};

export default ListRoles;
